/** @type {string[]} */
const CALLBACK_TIME_SLOTS = (() => {
	const slots = [];

	for (let hour = 9; hour <= 18; hour += 1) {
		for (const minute of [0, 30]) {
			if (18 === hour && 30 === minute) {
				continue;
			}

			slots.push(
				`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
			);
		}
	}

	return slots;
})();

/**
 * Contacts page callback form schedule toggle.
 *
 * @return {void}
 */
export function initContactCallbackForm() {
	document.querySelectorAll('[data-contact-callback-form]').forEach((block) => {
		if (block instanceof HTMLElement) {
			setupContactCallbackFormBlock(block);
		}
	});
}

/**
 * Binds schedule toggle behavior to a callback form block.
 *
 * @param {HTMLElement} block Callback form block.
 * @return {void}
 */
function setupContactCallbackFormBlock(block) {
	const time_field = block.querySelector('[data-callback-time-field]');

	if (!(time_field instanceof HTMLElement)) {
		return;
	}

	placeCallbackTimeField(block, time_field);

	const time_input = time_field.querySelector('input[name="callback-time"]');

	if (time_input instanceof HTMLInputElement) {
		initCallbackTimePicker(time_field, time_input);
	}

	if ('true' !== block.dataset.callbackFormBound) {
		block.dataset.callbackFormBound = 'true';

		block.addEventListener('change', (event) => {
			const target = event.target;

			if (
				target instanceof HTMLInputElement &&
				'callback-mode' === target.name
			) {
				syncCallbackTimeField(block, time_field);
			}
		});

		block.addEventListener('click', (event) => {
			const target = event.target;

			if (!(target instanceof Element)) {
				return;
			}

			if (
				target.closest(
					'[data-callback-schedule-options] .wpcf7-list-item label'
				)
			) {
				window.requestAnimationFrame(() => {
					syncCallbackTimeField(block, time_field);
				});
			}
		});

		document.addEventListener('click', (event) => {
			const target = event.target;

			if (
				target instanceof Element &&
				!target.closest('[data-callback-time-field]')
			) {
				closeCallbackTimeDropdown(time_field);
			}
		});
	}

	syncCallbackTimeField(block, time_field);
}

/**
 * Whether the scheduled callback option is currently selected.
 *
 * @param {HTMLElement} block Callback form block.
 * @return {boolean}
 */
function isScheduledCallbackSelected(block) {
	const radios = Array.from(
		block.querySelectorAll('input[name="callback-mode"]')
	).filter((radio) => radio instanceof HTMLInputElement);

	if (!radios.length) {
		return false;
	}

	const checked = radios.find((radio) => radio.checked);

	if (!(checked instanceof HTMLInputElement)) {
		return false;
	}

	if ('scheduled' === checked.value) {
		return true;
	}

	if ('now' === checked.value) {
		return false;
	}

	return checked === radios[radios.length - 1];
}

/**
 * Enables or disables the callback time field based on radio selection.
 *
 * @param {HTMLElement} block      Callback form block.
 * @param {HTMLElement} time_field   Time field wrapper.
 * @return {void}
 */
function syncCallbackTimeField(block, time_field) {
	const time_input = time_field.querySelector('input[name="callback-time"]');
	const is_scheduled = isScheduledCallbackSelected(block);

	time_field.classList.toggle('is-disabled', !is_scheduled);

	if (!(time_input instanceof HTMLInputElement)) {
		return;
	}

	if (is_scheduled) {
		time_input.disabled = false;
		time_input.removeAttribute('disabled');
		time_input.setAttribute('aria-disabled', 'false');
		openCallbackTimeDropdown(time_field, time_input);
		return;
	}

	closeCallbackTimeDropdown(time_field);
	time_input.disabled = true;
	time_input.setAttribute('aria-disabled', 'true');
	time_input.value = '';
}

/**
 * Places the time field inline after the scheduled callback radio option.
 *
 * @param {HTMLElement} block      Callback form block.
 * @param {HTMLElement} time_field Time field wrapper.
 * @return {void}
 */
function placeCallbackTimeField(block, time_field) {
	const scheduled_item = block
		.querySelector('input[name="callback-mode"][value="scheduled"]')
		?.closest('.wpcf7-list-item');

	if (scheduled_item instanceof HTMLElement) {
		scheduled_item.insertAdjacentElement('afterend', time_field);
		return;
	}

	const radios = block.querySelectorAll('input[name="callback-mode"]');
	const last_radio = radios[radios.length - 1];
	const last_item = last_radio?.closest('.wpcf7-list-item');

	if (last_item instanceof HTMLElement) {
		last_item.insertAdjacentElement('afterend', time_field);
		return;
	}

	const radio_group = block.querySelector(
		'[data-callback-schedule-options] .wpcf7-radio'
	);

	if (radio_group instanceof HTMLElement) {
		radio_group.appendChild(time_field);
	}
}

/**
 * Builds the callback time dropdown (stomatology contacts pattern).
 *
 * @param {HTMLElement} time_field   Time field wrapper.
 * @param {HTMLInputElement} input Native CF7 time input.
 * @return {void}
 */
function initCallbackTimePicker(time_field, input) {
	if ('true' === input.dataset.callbackTimePickerBound) {
		return;
	}

	input.dataset.callbackTimePickerBound = 'true';
	input.readOnly = true;
	input.setAttribute('autocomplete', 'off');
	input.classList.add('contact-callback-form__time-display');

	const options = document.createElement('div');
	options.className = 'contact-callback-form__time-options';
	options.setAttribute('role', 'listbox');
	options.setAttribute('aria-label', 'Время звонка');

	const body = document.createElement('div');
	body.className =
		'contact-callback-form__time-options-body contact-callback-form__scrollbar';

	CALLBACK_TIME_SLOTS.forEach((slot) => {
		const item = document.createElement('button');
		item.type = 'button';
		item.className = 'contact-callback-form__time-option';
		item.dataset.option = slot;
		item.setAttribute('role', 'option');
		item.textContent = slot;
		body.appendChild(item);
	});

	options.appendChild(body);
	time_field.appendChild(options);

	const openDropdown = () => {
		if (time_field.classList.contains('is-disabled')) {
			return;
		}

		openCallbackTimeDropdown(time_field, input);
	};

	input.addEventListener('click', openDropdown);
	input.addEventListener('focus', openDropdown);

	body.addEventListener('click', (event) => {
		const target = event.target;

		if (!(target instanceof HTMLElement)) {
			return;
		}

		const option = target.closest('.contact-callback-form__time-option');

		if (!(option instanceof HTMLButtonElement)) {
			return;
		}

		const value = option.dataset.option || option.textContent.trim();

		if (!value) {
			return;
		}

		input.value = value;
		input.dispatchEvent(new Event('input', { bubbles: true }));
		closeCallbackTimeDropdown(time_field);
		input.blur();
	});
}

/**
 * Opens the callback time dropdown.
 *
 * @param {HTMLElement} time_field Time field wrapper.
 * @param {HTMLInputElement} input Time input.
 * @return {void}
 */
function openCallbackTimeDropdown(time_field, input) {
	if (time_field.classList.contains('is-disabled')) {
		return;
	}

	time_field.classList.add('is-open');

	if (document.activeElement !== input) {
		input.focus();
	}
}

/**
 * Closes the callback time dropdown.
 *
 * @param {HTMLElement} time_field Time field wrapper.
 * @return {void}
 */
function closeCallbackTimeDropdown(time_field) {
	time_field.classList.remove('is-open');
}
